import React from 'react';
import styled from 'styled-components';
import {
  fontSize,
  fontWeights,
  minBreakpointQuery,
  sectionMargins,
  brandColours,
  fluidFontSize,
  breakpointSizes,
} from '../styles';
import { Container, HtmlContent } from './ui';
import EventListing from './EventListing';
import EventSidebar from './EventSidebar';
import { hasStructuredText } from '../utils';
import linkIcon from '../images/link-icon.svg';

const StyledEventVenueDetail = styled.div`
  ${sectionMargins('30px', '50px')};
`;

const StyledInner = styled.div`
  display: grid;
  gap: 40px;

  ${minBreakpointQuery.smedium`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${minBreakpointQuery.medium`
    grid-template-columns: .65fr .35fr;
    gap: 50px;
  `}

  ${minBreakpointQuery.mlarge`
    gap: 60px;
  `}
`;

const StyledHeading = styled.h2`
  font-weight: ${fontWeights.regular};
  ${fluidFontSize(
    '18px',
    '25px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};

  b {
    font-weight: ${fontWeights.regular};
    text-decoration: underline;
  }
`;

const StyledMainContent = styled.div``;

const StyledAbout = styled.div`
  margin-bottom: 30px;

  ${minBreakpointQuery.medium`
    margin-bottom: 40px;
  `}
`;

const StyledContent = styled(HtmlContent)`
  margin-top: 20px;

  ${minBreakpointQuery.medium`
    margin-bottom: 30px;
  `}
`;

const StyledListing = styled.div``;

const StyledText = styled.p`
  margin-top: 30px;
  ${fontSize(15)};

  ${minBreakpointQuery.small`
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.medium`
    margin-top: 40px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 50px;
  `}

  ${minBreakpointQuery.xlarge`
    margin-top: 60px;
  `}
`;

const StyledSidebar = styled(EventSidebar)`
  ${minBreakpointQuery.smedium`
    position: sticky;
    top: 20px; 
    height: fit-content; 
  `}
`;

const StyledAddress = styled.div``;

const StyledSubHeading = styled.h3`
  font-weight: ${fontWeights.semibold};
  ${fontSize(16)};
  margin-bottom: 4px;
`;

const StyledContact = styled.div`
  flex-shrink: 0;
  margin-top: 20px;
`;

const StyledLink = styled.a`
  display: block;
  margin-top: 22px;
  color: ${brandColours.primary};
  display: flex;
  align-items: flex-end;
  flex-wrap: nowrap;

  &:after {
    content: '';
    background: url(${linkIcon}) no-repeat;
    font-size: 0;
    height: 18px;
    width: 18px;
    display: block;
    margin-left: 14px;
  }
`;

const EventVenueDetail = ({
  content,
  items,
  cta,
  name,
  address,
  phoneNumber,
  website,
}) => {
  const mapAddress = address
    ? encodeURIComponent(
        `${name} + ${address.replaceAll(',', '+').replaceAll(/\s/g, '')}`,
      ).replaceAll('%2B', '+')
    : false;

  return (
    <StyledEventVenueDetail>
      <Container>
        <StyledInner>
          <StyledMainContent>
            {hasStructuredText(content) && (
              <StyledAbout>
                <StyledHeading>About {name}</StyledHeading>
                <StyledContent content={content} />
              </StyledAbout>
            )}
            <StyledListing>
              <StyledHeading>Upcoming Trade Shows at this Venue</StyledHeading>
              {items.length > 0 ? (
                <EventListing items={items} isVenuePage={true} />
              ) : (
                <StyledText>
                  There are no exhibitions scheduled for this venue at the
                  moment. Please check back for future updates.
                </StyledText>
              )}
            </StyledListing>
          </StyledMainContent>
          <StyledSidebar mapAddress={mapAddress} cta={cta} isVenuePage={true}>
            {address && (
              <StyledAddress>
                <StyledSubHeading>Venue Address</StyledSubHeading>
                <p>{address}</p>
              </StyledAddress>
            )}
            {(phoneNumber || website) && (
              <StyledContact>
                {phoneNumber && (
                  <>
                    <StyledSubHeading>Venue Phone Number</StyledSubHeading>
                    <p>{phoneNumber}</p>
                  </>
                )}
                {website && (
                  <StyledLink href={website} target="_blank" rel="noopener">
                    Visit venue website
                  </StyledLink>
                )}
              </StyledContact>
            )}
          </StyledSidebar>
        </StyledInner>
      </Container>
    </StyledEventVenueDetail>
  );
};

export default EventVenueDetail;
